import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import React, { CSSProperties, HTMLProps } from 'react'

import { Background } from '@Components/Background'
import { Chip } from '@Components/Chips/Chip/Chip'
import { Link } from '@Components/Link'
import { RTEContent } from '@Components/RTEContent'
import { BlockAppearance, BlockCommon, PagePresentation, ReferenceContentFragment } from '@Generated/graphql'
import { getPageReferences } from '@Globals/CMS/FieldHelpers'

import styles from './styles.module.scss'

const ConditionalContentInnerWrapper = ({
  children,
  condition,
}: {
  children: React.ReactNode
  condition: boolean
}) => (condition
  ? <div className={clsx(styles.contentInner, 'contentInner')}>{children}</div>
  : children)

export const BlockContainer = ({
  appearance,
  center,
  children,
  className,
  common,
  commonWidth,
  heightRestriction,
  noPadding,
  presentation,
  style,
  titleOverride,
  ...props
}: {
  appearance: BlockAppearance
  center?: boolean
  children?: React.ReactNode
  className?: string
  common: BlockCommon
  commonWidth?: string
  heightRestriction?: boolean
  noPadding?: boolean
  presentation?: PagePresentation
  style?: CSSProperties
  titleOverride?: React.ReactNode
} & HTMLProps<HTMLDivElement>) => {
  const { asset, backgroundColor, textColor } = appearance
  const { chips, label, position, settings: settingsRaw, text, title, titleElement, titleLink } = common
  const settings = (typeof settingsRaw === 'object' ? settingsRaw || {} : {}) as Record<string, string>

  const TitleElement = (titleElement || 'p') as keyof React.JSX.IntrinsicElements

  return (
    <div
      className={clsx(
        styles.container,
        center && styles.center,
        styles[position || 'top'],
        backgroundColor && styles.hasBackgroundColor,
        asset && styles.hasAsset,
        presentation === PagePresentation.Navigation && styles.inNavigation,
        noPadding && styles.noPadding,
        heightRestriction && styles.heightRestriction,
        className,
      )}
      style={{
        ...style,
        '--background-color': backgroundColor,
        '--text-color': textColor,
      } as CSSProperties}
      {...props}
    >
      {asset && (
        <Background asset={asset as ReferenceContentFragment} />
      )}
      <div className={clsx(styles.inner, 'innerBlockContainer')}>
        {(titleOverride || !isEmpty(title)) && (
          <div className={clsx(
            styles.content,
            commonWidth && styles[commonWidth],
          )}
          >
            <ConditionalContentInnerWrapper
              condition={commonWidth === 'half'}
            >
              {label && (
                <Chip
                  backgroundColor={label.appearance?.backgroundColor || 'rgba(21, 26, 28, 0.3)'}
                  className={styles.label}
                  color={label.text?.value?.toLowerCase()}
                  size="large"
                  style={{ fontFamily: `var(--font-${settings?.labelFont || 'gotham'})` }}
                  textColor={label.appearance?.textColor || 'var(--white)'}
                >
                  {label.text?.value}
                </Chip>
              )}
              {!titleOverride && title && isString(title) && (
                <TitleElement
                  className={styles.title}
                  style={{ fontFamily: `var(--font-${settings?.titleFont || 'gotham'})` }}
                >
                  {titleLink?.url
                    ? (
                      <Link
                        className={clsx(styles.titleLink, 'blockTitleLink')}
                        href={titleLink.url}
                        target={titleLink.newTab ? '_blank' : undefined}
                      >
                        {title}
                      </Link>
                    )
                    : title}
                </TitleElement>
              )}
              {titleOverride && (
                <TitleElement
                  className={styles.title}
                  style={{ fontFamily: `var(--font-${settings?.titleFont || 'gotham'})` }}
                >
                  {titleOverride}
                </TitleElement>
              )}
              {text && (
                <div
                  className={styles.text}
                  style={{ fontFamily: `var(--font-${settings?.textFont || 'gotham'})` }}
                >
                  <RTEContent data={text} />
                </div>
              )}
              {!isEmpty(chips) && (
                <div className={styles.chips}>
                  {chips?.map(chip => (
                    <Link
                      key={`chip__${chip.link?.id}`}
                      className={styles.chip}
                      href={chip.link?.url || ''}
                      style={{
                        backgroundColor: chip.appearance?.backgroundColor || '',
                        borderColor: chip.appearance?.backgroundColor ? 'transparent' : '',
                        color: chip.appearance?.textColor || '',
                        fontFamily: `var(--font-${settings?.chipsFont || 'gotham'})`,
                      }}
                      target={chip.link!.newTab ? '_blank' : undefined}
                    >
                      <Background asset={chip.appearance?.asset as ReferenceContentFragment} />
                      {chip.link?.overrideTitle || getPageReferences(chip.link?.references || [])?.[0].title || ''}
                    </Link>
                  ))}
                </div>
              )}
            </ConditionalContentInnerWrapper>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
